import { useParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  ArrowLeft, 
  Calendar,
  Check,
  Clock,
  MapPin,
  Phone,
  Star,
  Video,
  PhoneCall,
  Users
} from "lucide-react";

const BOOKING_URL = "https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ2sLR40fV8O-hfoDyzVRQq1wTbDNf0T-8Ovy79yBcvaGSRyMCmuzMMj_fEnqs6AjhDQlKyeVbLd";

const SERVICE_DETAILS = {
  "doula-direct": {
    name: "Doula Direct™",
    tagline: "Direct access to trusted support when you need guidance and answers",
    heroImage: "https://static.prod-images.emergentagent.com/jobs/1e730d40-55a8-4421-8dbe-a02d71b76db0/images/7f1a7fc6026cbb5825dfff6a15adfdd02acce629d576cf65de3d8238593ebefe.png",
    description: `Get direct access to compassionate doula support for pregnancy, birth, and postpartum. Schedule a virtual or phone session when you need personalized guidance.`,
    tiers: [
      {
        id: "30-min",
        name: "Doula Direct™ Call",
        duration: "30 Minutes",
        price: "$75",
        delivery: ["Virtual", "Phone"],
        description: "A focused session for quick questions, check-ins, and immediate guidance.",
        includes: [
          "30-minute one-on-one session via Zoom or phone",
          "Pregnancy, birth prep, postpartum, or newborn care topics",
          "Personalized recommendations and next steps",
          "Follow-up resource email within 24 hours"
        ]
      },
      {
        id: "60-min",
        name: "Doula Direct™ Extended",
        duration: "60 Minutes",
        price: "$125",
        delivery: ["Virtual", "Phone"],
        description: "A deeper session for more complex concerns, birth planning, or emotional support.",
        includes: [
          "60-minute in-depth session via Zoom or phone",
          "Comprehensive support on any perinatal topic",
          "Birth plan review or creation assistance",
          "Comfort measure demonstrations and techniques",
          "Detailed follow-up resource guide within 24 hours"
        ]
      },
      {
        id: "monthly",
        name: "Doula Direct™ Monthly",
        duration: "Ongoing Monthly",
        price: "$277/mo",
        delivery: ["Virtual", "Phone"],
        description: "Comprehensive monthly support combining doula care and nursing expertise on alternating weeks, so you always have someone in your corner.",
        includes: [
          "2 biweekly 90-minute meetings with your Doula",
          "2 biweekly 30-minute meetings with a Nurse on alternate weeks",
          "Priority scheduling and same-week availability",
          "Unlimited text/email support between sessions",
          "Personalized wellness and nutrition guidance",
          "Birth preparation and postpartum planning",
          "Cancel or pause anytime"
        ]
      }
    ],
    idealFor: [
      "Expectant parents with questions between OB/midwife visits",
      "Postpartum mothers needing guidance on newborn care",
      "Anyone wanting professional doula support without a full package commitment",
      "Families seeking virtual support from anywhere in the country",
      "Partners looking for coaching on how to support during birth or postpartum"
    ]
  },
  "10-visit-package": {
    name: "10-Visit Support Package",
    tagline: "Personalized support designed around your family's needs",
    heroImage: "https://images.pexels.com/photos/6392989/pexels-photo-6392989.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750",
    description: `The 10-Visit Support Package is designed for families who want consistent, personalized support throughout their journey. Whether you're preparing for birth, navigating the postpartum period, or adjusting to life with a newborn these 10 visits are tailored entirely to your needs.

Each visit can be virtual, by phone, or in-person giving you the flexibility to get support however works best for your life. This package is ideal for families who want more than a single session but want the freedom to use their visits across pregnancy, birth prep, and postpartum.`,
    pricing: "$1,200",
    duration: "10 visits, flexible scheduling",
    format: "Virtual, Phone, or In-Person",
    whatToExpect: [
      "10 personalized support visits (60 minutes each)",
      "Package must be completed within 3 months",
      "Flexible scheduling use visits across pregnancy and postpartum",
      "Choose virtual, phone, or in-person for each visit",
      "Customized support plan based on your family's priorities",
      "Birth preparation and comfort measure education",
      "Postpartum recovery and newborn care guidance",
      "Nutrition and wellness recommendations",
      "Text/email support between visits",
      "Breastfeeding and lactation support",
      "Partner coaching and family integration support"
    ],
    idealFor: [
      "Families wanting consistent support across pregnancy and postpartum",
      "Those who prefer short term flexible care",
      "Parents without nearby family support systems",
      "Anyone wanting in-person visits combined with virtual check-ins",
      "Families preparing for their first or subsequent babies"
    ]
  },
  "birth-doula": {
    name: "Labor Doula Services",
    tagline: "Continuous support from pregnancy through birth and beyond",
    heroImage: "https://images.pexels.com/photos/6393368/pexels-photo-6393368.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750",
    description: `Birth is one of life's most transformative experiences, and you deserve unwavering support throughout your journey. Our Labor Doula Services provide comprehensive care that begins during pregnancy and continues through labor, delivery, and the early postpartum period.

As your labor doula, I serve as your advocate, guide, and continuous source of comfort. I help you navigate decisions, provide evidence-based information, and ensure your voice is heard throughout the birthing process. My role is to support both you and your partner, creating a calm and empowering environment no matter how your birth unfolds.`,
    pricing: "Contact for package pricing",
    duration: "Prenatal through postpartum",
    format: "In-Person + Virtual support",
    whatToExpect: [
      "Prenatal visits to discuss your birth preferences and create a birth plan",
      "Comprehensive childbirth education covering stages of labor and comfort measures",
      "24/7 on-call availability starting at 38 weeks",
      "Continuous physical and emotional support during labor and delivery",
      "Comfort techniques including positioning, breathing, massage, and relaxation",
      "Advocacy and communication support with medical staff",
      "Partner coaching and support throughout the process",
      "Postpartum follow-up visit to process your birth experience"
    ],
    idealFor: [
      "First-time parents wanting guidance and support",
      "Couples seeking a more informed and empowered birth experience",
      "Those planning natural births who want continuous labor support",
      "Parents who had previous challenging births and want additional support",
      "Anyone who wants a dedicated advocate during their birth experience"
    ]
  },
  "postpartum-doula": {
    name: "Postpartum Doula Services",
    tagline: "Nurturing support for mothers and families in the fourth trimester",
    heroImage: "https://images.pexels.com/photos/3845456/pexels-photo-3845456.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750",
    description: `The postpartum period often called the "fourth trimester" is a sacred time of recovery, bonding, and adjustment. Our postpartum doula services provide in-home support that allows you to rest, heal, and connect with your baby while receiving the nurturing care every new parent deserves.

As your postpartum doula, I come to your home to provide practical support and infant care assistance. This isn't just about helping with tasks it's about ensuring you feel confident, supported, and cared for during one of the most vulnerable and beautiful times in your life.`,
    pricing: "Contact for pricing and packages",
    duration: "Flexible scheduling (daytime shifts)",
    format: "In-Home visits",
    whatToExpect: [
      "In-home postpartum visits tailored to your schedule and needs",
      "Newborn care support including diapering, bathing, and soothing techniques",
      "Breastfeeding and bottle-feeding support and troubleshooting",
      "Light household tasks so you can focus on rest and bonding",
      "Emotional support and check-ins on your mental wellness",
      "Education on infant cues, sleep patterns, and developmental milestones",
      "Meal preparation and nutrition support",
      "Sibling adjustment support if applicable"
    ],
    idealFor: [
      "New parents recovering from vaginal or cesarean birth",
      "Families who want hands-on newborn care education",
      "Families without nearby support systems",
      "Parents experiencing breastfeeding challenges",
      "Anyone who wants dedicated time to rest while baby is cared for"
    ]
  },
  "childbirth-education": {
    name: "Childbirth Education",
    tagline: "Comprehensive education from pregnancy through baby's first year",
    heroImage: "https://images.pexels.com/photos/6393369/pexels-photo-6393369.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750",
    description: `Knowledge is power, especially when preparing for parenthood. Our comprehensive Childbirth Education covers everything you need to know from healthy pregnancy practices through your baby's first year of life. This course provides evidence-based education in an accessible, supportive format.

Whether you're a first-time parent feeling overwhelmed by information or an experienced parent wanting a refresher, this class gives you the confidence and skills to navigate pregnancy, birth, and early parenthood. Learn at your own pace with content that respects your intelligence while remaining practical and applicable.`,
    pricing: "Contact for pricing and enrollment",
    duration: "Self-paced or scheduled sessions",
    format: "Virtual / Online",
    whatToExpect: [
      "Comprehensive curriculum covering the full perinatal period",
      "Healthy pregnancy practices including nutrition and movement",
      "Labor and delivery preparation including stages of labor and comfort measures",
      "Lactation education covering breastfeeding basics and common challenges",
      "Bottle feeding support and combination feeding guidance",
      "Infant care essentials: diapering, bathing, soothing, safe sleep",
      "Understanding infant development in the first year",
      "Postpartum recovery and maternal wellness"
    ],
    idealFor: [
      "First-time expectant parents",
      "Parents wanting comprehensive, cohesive education",
      "Those who prefer learning virtually at their own pace",
      "Couples who want to learn together",
      "Grandparents or caregivers seeking updated information"
    ]
  },
  "family-support": {
    name: "Family Support Services",
    tagline: "Holistic support for the whole family during life's biggest transitions",
    heroImage: "https://images.pexels.com/photos/7328445/pexels-photo-7328445.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    description: `When your family is growing, everyone is affected not just the new parents. Our Family Support Services provide holistic care that extends to siblings, partners, and the entire household. From sibling care during labor and delivery to healthy meal preparation and family wellness support, we ensure your whole family feels supported through this transition.

Support changes the entire journey. We're here to make sure no one in your family has to figure it out alone.`,
    pricing: "Contact for pricing",
    duration: "Flexible scheduling",
    format: "In-Home + Virtual",
    whatToExpect: [
      "Sibling care during labor and delivery on-call availability",
      "In-home care maintaining children's routines (meals, naps, bedtime)",
      "Age-appropriate activities and engagement for older children",
      "Healthy meal preparation using your kitchen",
      "Menu planning based on dietary needs and preferences",
      "Batch cooking for multiple meals throughout the week",
      "Postpartum-focused recipes that support recovery and lactation",
      "Help preparing siblings for meeting the new baby",
      "Family wellness check-ins and coordination"
    ],
    idealFor: [
      "Families with young children at home during birth",
      "Parents whose usual support people will be at the birth",
      "Busy families wanting healthy meals without the prep work",
      "Those with dietary restrictions needing specialized meals",
      "Families wanting comprehensive household support during postpartum"
    ]
  }
};

const ServiceDetailPage = () => {
  const { serviceId } = useParams();
  const service = SERVICE_DETAILS[serviceId];

  if (!service) {
    return (
      <div className="min-h-screen bg-warm-white flex items-center justify-center pt-24">
        <div className="text-center">
          <h1 className="font-heading text-4xl text-plum mb-4">Service Not Found</h1>
          <Link to="/#services">
            <Button className="bg-teal hover:bg-teal-dark text-white rounded-full">
              View All Services
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Doula Direct™ has tiered pricing
  const isDoulaDirectPage = serviceId === "doula-direct";

  return (
    <div className="min-h-screen bg-warm-white">
      {/* Hero Section */}
      <section className="relative h-[40vh] sm:h-[50vh] md:h-[60vh] min-h-[300px] sm:min-h-[350px] md:min-h-[400px]">
        <div className="absolute inset-0">
          <img 
            src={service.heroImage} 
            alt={service.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-plum/80 via-plum/40 to-transparent"></div>
        </div>
        <div className="relative z-10 h-full flex flex-col justify-end max-w-7xl mx-auto px-5 sm:px-6 md:px-12 pb-8 sm:pb-12 md:pb-16">
          <Link 
            to="/#services" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4 md:mb-6 transition-colors w-fit"
            data-testid="back-to-services"
          >
            <ArrowLeft className="w-4 h-4 md:w-5 md:h-5" />
            <span className="font-body text-sm md:text-base">Back to Services</span>
          </Link>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-semibold text-white mb-2 md:mb-4">
            {service.name}
          </h1>
          <p className="font-body text-base sm:text-lg md:text-xl text-white/90 max-w-2xl">
            {service.tagline}
          </p>
        </div>
      </section>

      {/* Quick Info Bar (non-tiered services) */}
      {!isDoulaDirectPage && (
        <section className="bg-clay/50 py-4 md:py-6">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12">
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-8 justify-center md:justify-start">
              <div className="flex items-center gap-2 sm:gap-3">
                <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-teal shrink-0" />
                <span className="font-body text-sm sm:text-base text-plum">{service.duration}</span>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-teal shrink-0" />
                <span className="font-body text-sm sm:text-base text-plum">{service.format}</span>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <Star className="w-4 h-4 sm:w-5 sm:h-5 text-teal shrink-0" />
                <span className="font-body text-sm sm:text-base text-plum font-semibold">{service.pricing}</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Main Content */}
      <section className="py-10 md:py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12">
          <div className="grid lg:grid-cols-3 gap-8 md:gap-12">
            {/* Description */}
            <div className="lg:col-span-2 space-y-8 md:space-y-12">
              <div>
                <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl text-plum mb-4 md:mb-6">
                  About This Service
                </h2>
                <div className="font-body text-plum/80 leading-relaxed space-y-4">
                  {service.description.split('\n\n').map((paragraph, idx) => (
                    <p key={idx}>{paragraph}</p>
                  ))}
                </div>
              </div>

              {/* Doula Direct™ Tiers */}
              {isDoulaDirectPage && service.tiers && (
                <div>
                  <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl text-plum mb-6 md:mb-8">
                    Choose Your Level of Support
                  </h2>
                  <div className="space-y-6">
                    {service.tiers.map((tier) => (
                      <div 
                        key={tier.id}
                        id={tier.id}
                        data-testid={`tier-${tier.id}`}
                        className="bg-white rounded-2xl p-6 md:p-8 border-2 border-peach/50 shadow-sm"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                          <h3 className="font-heading text-xl md:text-2xl font-medium text-plum">{tier.name}</h3>
                          <span className="font-heading text-2xl font-semibold text-teal">{tier.price}</span>
                        </div>
                        <p className="font-body text-plum/70 mb-2">{tier.description}</p>
                        <div className="flex gap-2 mb-4">
                          {tier.delivery.map((d) => (
                            <span key={d} className="inline-flex items-center gap-1 bg-teal/10 text-teal text-xs font-body font-semibold px-3 py-1 rounded-full">
                              {d === "Virtual" ? <Video className="w-3 h-3" /> : <PhoneCall className="w-3 h-3" />}
                              {d}
                            </span>
                          ))}
                        </div>
                        <ul className="space-y-2 mb-6">
                          {tier.includes.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <Check className="w-4 h-4 text-teal shrink-0 mt-0.5" />
                              <span className="font-body text-sm text-plum/80">{item}</span>
                            </li>
                          ))}
                        </ul>
                        <a href="https://go.peoplesmedicine.online/checkout" target="_blank" rel="noopener noreferrer">
                          <Button className="bg-teal hover:bg-teal-dark text-white rounded-full px-6">
                            <Calendar className="w-4 h-4 mr-2" />
                            Book This Session
                          </Button>
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* What to Expect (non-tiered) */}
              {!isDoulaDirectPage && service.whatToExpect && (
                <div>
                  <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl text-plum mb-4 md:mb-6">
                    What's Included
                  </h2>
                  <ul className="space-y-4">
                    {service.whatToExpect.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-teal/10 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                          <Check className="w-4 h-4 text-teal" />
                        </div>
                        <span className="font-body text-plum/80">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Ideal For */}
              {service.idealFor && (
                <div>
                  <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl text-plum mb-4 md:mb-6">
                    This Service is Ideal For
                  </h2>
                  <ul className="space-y-3">
                    {service.idealFor.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Users className="w-5 h-5 text-teal shrink-0 mt-0.5" />
                        <span className="font-body text-plum/80">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Sidebar CTA */}
            <div className="lg:col-span-1">
              <div className="lg:sticky lg:top-24 bg-white rounded-3xl p-6 sm:p-8 shadow-lg border border-peach/30">
                <h3 className="font-heading text-2xl text-plum mb-4">
                  Ready to Get Started?
                </h3>
                <p className="font-body text-plum/70 mb-6">
                  Support changes the entire journey. Schedule a call to discuss your needs and find the right fit.
                </p>
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="block">
                  <Button 
                    data-testid="service-detail-book-btn"
                    className="w-full bg-teal hover:bg-teal-dark text-white rounded-full py-5 sm:py-6 text-base sm:text-lg transition-all duration-300"
                  >
                    <Calendar className="w-5 h-5 mr-2" />
                    Schedule a Support Call
                  </Button>
                </a>
                
                <div className="mt-8 pt-6 border-t border-peach/30">
                  <p className="font-body text-sm text-plum/60 mb-4">Have questions?</p>
                  <a 
                    href="tel:+18565675907" 
                    className="flex items-center gap-2 text-teal hover:underline font-body"
                  >
                    <Phone className="w-4 h-4" />
                    (856) 567-5907
                  </a>
                </div>

                <div className="mt-6 p-4 bg-teal/5 rounded-xl">
                  <p className="font-body text-sm text-plum/70">
                    <strong className="text-plum">Available:</strong> Virtual & phone support nationwide. In-person across Maryland, DC & Virginia.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Services */}
      <section className="py-10 md:py-16 bg-clay/30">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12">
          <h2 className="font-heading text-2xl sm:text-3xl text-plum mb-6 md:mb-8 text-center">
            Explore Other Services
          </h2>
          <div className="flex flex-wrap justify-center gap-3 md:gap-4">
            {Object.entries(SERVICE_DETAILS)
              .filter(([id]) => id !== serviceId)
              .slice(0, 4)
              .map(([id, svc]) => (
                <Link 
                  key={id} 
                  to={`/services/${id}`}
                  className="bg-white px-4 sm:px-6 py-2 sm:py-3 rounded-full font-body text-sm sm:text-base text-plum hover:bg-teal hover:text-white transition-all duration-300 shadow-sm"
                >
                  {svc.name}
                </Link>
              ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/#services">
              <Button variant="outline" className="rounded-full border-plum/30 text-plum hover:bg-plum/5">
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetailPage;
